/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { combineReducers, configureStore } from "@reduxjs/toolkit";

import { userReducer } from "./user/userReducer";
import { roleReducer } from "./role/roleReducer";
import { authReducer } from "./auth/authReducer";
import { errorReducer } from "./error/errorReducer";
import { loaderReducer } from "./loader/loaderReducer";
import { machineReducer } from "./machine/machineReducer";
import { dataReducer } from "./role/PolicyData/dataReducer";
import { snackBarReducer } from "./snackBar/snackBarReducer";
import { appLoaderReducer } from "./appLoader/appLoaderReducer";
import { locationsReducer } from "./locations/locationsReducer";
import { entityReducer } from "./role/policyEntity/entityReducer";
import { saveUserReducer } from "./user/saveUser/saveUserReducer";
import { userGroupsReducer } from "./userGroups/userGroupsReducer";
import { measureReducer } from "./role/PolicyMeasure/measureReducer";
import { appLocationReducer } from "./appLocation/appLocationReducer";
import { globalRoleReducer } from "./role/GlobalRole/globalRoleReducer";
import { locationTypesReducer } from "./locations/locationTypesReducer";
import { identityProvidersReducer } from "./idp/identityProvidersReducer";
import { dimensionReducer } from "./role/policyDimension/dimensionReducer";
import { realmSettingsReducer } from "./realmSettings/realmSettingsReducer";
import { functionalReducer } from "./role/PolicyFunctional/functionalReducers";
import { assignedUsersReducer } from "./user/assignedUsers/assignedUsersReducer";
import { builtinPoliciesReducer } from "./builtinPolicies/builtinPoliciesReducer";
import { locationToRoleReducer } from "./role/LocationToRole/locationToRoleReducer";
import { passwordPoliciesReducer } from "./passwordPolicies/passwordPoliciesReducer";

export const rootReducer = combineReducers({
  user: userReducer,
  role: roleReducer,
  machine: machineReducer,
  loader: loaderReducer,
  error: errorReducer,
  auth: authReducer,
  appLoader: appLoaderReducer,
  functional: functionalReducer,
  policyData: dataReducer,
  snackBar: snackBarReducer,
  policyEntities: entityReducer,
  saveUser: saveUserReducer,
  policyDimension: dimensionReducer,
  measurePolicy: measureReducer,
  assignedUsers: assignedUsersReducer,
  builtinPolicies: builtinPoliciesReducer,
  idp: identityProvidersReducer,
  globalRole: globalRoleReducer,
  locations: locationsReducer,
  locationTypes: locationTypesReducer,
  passwordPolicies: passwordPoliciesReducer,
  appLocation: appLocationReducer,
  locationToRole: locationToRoleReducer,
  realmSettings: realmSettingsReducer,
  userGroups: userGroupsReducer,
});

export const store = (preloadedState?: any) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
  });
};

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof store>;
export type AppDispatch = AppStore["dispatch"];